import React, { useRef, useEffect } from 'react';
import { gsap } from '../../lib/gsapSetup';
import { useTranslation } from '../../i18n/LanguageContext';
import playstoreIcon from '../../assets/playstore.png';
import appStoreIcon from '../../assets/app-store.png';

/**
 * DOWNLOAD CTA — Cinematic closing section.
 * Animation: dramatic zoom-in reveal with staggered elements,
 * glow pulse, and badges sliding from sides.
 */
export default function DownloadCTA() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const contentRef = useRef(null);
  const bgRef = useRef(null);
  const glowRef = useRef(null);
  const badgeLeftRef = useRef(null);
  const badgeRightRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 70%',
          end: 'top 20%',
          scrub: 1,
        },
      });

      // Heading: scale from small + blur dissolve
      tl.fromTo(
        headingRef.current,
        { opacity: 0, scale: 0.6, filter: 'blur(20px)' },
        {
          opacity: 1,
          scale: 1,
          filter: 'blur(0px)',
          duration: 1.2,
          ease: 'power2.out',
        },
        0,
      );

      // Glow pulse expands
      if (glowRef.current) {
        tl.fromTo(
          glowRef.current,
          { opacity: 0, scale: 0.3 },
          { opacity: 1, scale: 1, duration: 1.5, ease: 'power2.out' },
          0,
        );
      }

      // Subtitle fades up
      const subtitle = contentRef.current?.querySelector('.cta-subtitle');
      if (subtitle) {
        tl.fromTo(
          subtitle,
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' },
          0.4,
        );
      }

      // Badge Left slides from left
      if (badgeLeftRef.current) {
        tl.fromTo(
          badgeLeftRef.current,
          { opacity: 0, x: -80, rotate: -5 },
          { opacity: 1, x: 0, rotate: 0, duration: 0.8, ease: 'power3.out' },
          0.6,
        );
      }

      // Badge Right slides from right
      if (badgeRightRef.current) {
        tl.fromTo(
          badgeRightRef.current,
          { opacity: 0, x: 80, rotate: 5 },
          { opacity: 1, x: 0, rotate: 0, duration: 0.8, ease: 'power3.out' },
          0.7,
        );
      }

      // Bottom text fades in
      const bottomText = contentRef.current?.querySelector('.cta-bottom');
      if (bottomText) {
        tl.fromTo(
          bottomText,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
          0.9,
        );
      }

      // Background parallax
      if (bgRef.current) {
        gsap.to(bgRef.current, {
          yPercent: 25,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id='download'
      className='relative py-32 lg:py-40 overflow-hidden bg-[#0A174E]'
    >
      {/* Background parallax image */}
      <div className='absolute inset-0 overflow-hidden'>
        <img
          ref={bgRef}
          src='https://images.pexels.com/photos/4391478/pexels-photo-4391478.jpeg?auto=compress&cs=tinysrgb&w=1400&h=900&dpr=2'
          alt=''
          className='absolute inset-0 w-full h-[140%] object-cover opacity-[0.18] will-change-transform'
          loading='lazy'
        />
        <div className='absolute inset-0 bg-gradient-to-t from-[#0A174E] via-[#0A174E]/60 to-[#0A174E]/90' />
      </div>

      {/* Accent line */}
      <div className='accent-line relative z-10' />

      {/* Animated glow pulse */}
      <div
        ref={glowRef}
        className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none'
        style={{
          background:
            'radial-gradient(circle, rgba(253,190,52,0.1) 0%, rgba(253,190,52,0.03) 50%, transparent 70%)',
          opacity: 0,
        }}
      />

      <div className='section-container relative z-10'>
        <div ref={contentRef} className='max-w-3xl mx-auto text-center'>
          <h2
            ref={headingRef}
            className='font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8'
            aria-label={t('download.heading')}
          >
            {t('download.heading')}
          </h2>

          <p className='cta-subtitle text-lg text-white/40 leading-relaxed mb-12 max-w-xl mx-auto'>
            {t('download.subtitle')}
          </p>

          {/* Two app cards */}
          <div className='flex flex-col sm:flex-row items-stretch justify-center gap-5 mb-10'>

            {/* Client App */}
            <div
              ref={badgeLeftRef}
              className='flex-1 max-w-xs mx-auto sm:mx-0 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 text-center'
            >
              <p className='text-[11px] uppercase tracking-[0.2em] text-secondary mb-1'>{t('download.clientApp')}</p>
              <h3 className='text-white font-display font-semibold text-lg mb-5'>LinxExpress Client</h3>
              <div className='flex flex-col gap-3'>
                <a
                  href='https://play.google.com/store/apps/details?id=com.linxexpress.client'
                  target='_blank' rel='noopener noreferrer'
                  className='inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-secondary text-primary hover:brightness-110 transition-all duration-200 shadow-lg shadow-secondary/20'
                >
                  <img src={playstoreIcon} alt='Google Play' className='w-6 h-6 flex-shrink-0' />
                  <div className='text-left leading-none'>
                    <div className='text-[10px] opacity-60 uppercase tracking-widest mb-0.5'>{t('download.googlePlay.label')}</div>
                    <div className='text-sm font-semibold'>Google Play</div>
                  </div>
                </a>
                <a
                  href='https://apps.apple.com/app/linxexpress-client'
                  target='_blank' rel='noopener noreferrer'
                  className='inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white/8 border border-white/15 text-white hover:bg-white/15 hover:border-white/25 transition-all duration-200'
                >
                  <img src={appStoreIcon} alt='App Store' className='w-6 h-6 flex-shrink-0' />
                  <div className='text-left leading-none'>
                    <div className='text-[10px] opacity-50 uppercase tracking-widest mb-0.5'>{t('download.appStore.label')}</div>
                    <div className='text-sm font-semibold'>App Store</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Driver App */}
            <div
              ref={badgeRightRef}
              className='flex-1 max-w-xs mx-auto sm:mx-0 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 text-center'
            >
              <p className='text-[11px] uppercase tracking-[0.2em] text-secondary mb-1'>{t('download.driverApp')}</p>
              <h3 className='text-white font-display font-semibold text-lg mb-5'>LinxExpress Chauffeur</h3>
              <div className='flex flex-col gap-3'>
                <a
                  href='https://play.google.com/store/apps/details?id=com.linxexpress.driver'
                  target='_blank' rel='noopener noreferrer'
                  className='inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-secondary text-primary hover:brightness-110 transition-all duration-200 shadow-lg shadow-secondary/20'
                >
                  <img src={playstoreIcon} alt='Google Play' className='w-6 h-6 flex-shrink-0' />
                  <div className='text-left leading-none'>
                    <div className='text-[10px] opacity-60 uppercase tracking-widest mb-0.5'>{t('download.googlePlay.label')}</div>
                    <div className='text-sm font-semibold'>Google Play</div>
                  </div>
                </a>
                <a
                  href='https://apps.apple.com/app/linxexpress-driver'
                  target='_blank' rel='noopener noreferrer'
                  className='inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white/8 border border-white/15 text-white hover:bg-white/15 hover:border-white/25 transition-all duration-200'
                >
                  <img src={appStoreIcon} alt='App Store' className='w-6 h-6 flex-shrink-0' />
                  <div className='text-left leading-none'>
                    <div className='text-[10px] opacity-50 uppercase tracking-widest mb-0.5'>{t('download.appStore.label')}</div>
                    <div className='text-sm font-semibold'>App Store</div>
                  </div>
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
